"use client";

import { useCallback, useEffect, useState } from "react";
import { getLifestyleCountdownHeight } from "../lifestyle-sticky";
import { getStickyHeaderHeight } from "@/app/utils/stickyHeader";

/** DOM id of a collection's carousel row, the target of the jump links. */
export function collectionAnchorId(collectionId: number): string {
  return `lifestyle-collection-${collectionId}`;
}

/** DOM id of the sticky filter bar, measured so a jump clears it. */
export const FILTER_BAR_ID = "lifestyle-filter-bar";

/** Breathing room between the filter bar and the row heading it lands on. */
const SCROLL_GAP = 12;

/** Scrolls so a page section sits just below the sticky header stack. */
export function scrollToPageSection(elementId: string): boolean {
  const target = document.getElementById(elementId);
  if (!target) return false;

  const headerHeight = getStickyHeaderHeight();
  const countdownHeight = getLifestyleCountdownHeight();
  const barHeight = document.getElementById(FILTER_BAR_ID)?.getBoundingClientRect().height ?? 0;
  const top =
    target.getBoundingClientRect().top + window.scrollY - headerHeight - countdownHeight - barHeight - SCROLL_GAP;

  window.scrollTo({ top, behavior: "smooth" });
  return true;
}

/**
 * Backs the Collections tab of the filter modal, where picking a collection
 * scrolls to its carousel row rather than filtering down to it.
 *
 * The rows only exist in the unfiltered view, so a jump clears the filters
 * first and defers the scroll to the render that brings the row back. The
 * offset is measured from the live header and filter bar rather than
 * hardcoded, because the bar stacks on mobile and is several times taller
 * there.
 */
export default function useCollectionScroll(isFiltering: boolean, clearFilters: () => void) {
  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    if (!pendingId) return;

    // Still filtered, so the rows are not mounted yet — clearing the filters
    // re-runs this effect once they are.
    if (!scrollToPageSection(collectionAnchorId(pendingId))) return;

    setPendingId(null);
  }, [pendingId, isFiltering]);

  const scrollToCollection = useCallback(
    (collectionId: number) => {
      clearFilters();
      setPendingId(collectionId);
    },
    [clearFilters]
  );

  return scrollToCollection;
}
